import { MetricWithAttribution, onCLS, onINP, onLCP } from 'web-vitals/attribution'
import Router from 'next/router'

export const initWebVitalsReporting = () => {
  onCLS(onReport)
  onINP(onReport, { reportAllChanges: true })
  onLCP(onReport)

  Router.events.on('routeChangeComplete', () => {
    recordPageTransition(Router.asPath)
    resetWebVitalsMetrics()
  })
}

const onReport = (metric: MetricWithAttribution) => {
  const location = getPageLocationAt(metric.entries[0]?.startTime)

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!window.gtag) {
    // 想定外
    return false
  }
  gtag('event', metric.name, {
    value: metric.delta,
    metric_id: metric.id,
    metric_value: metric.value,
    metric_delta: metric.delta,
    ...getDebugInfo(metric),
    page_location: location,
  })
}

const getDebugInfo = (metric: MetricWithAttribution) => {
  if (metric.attribution as unknown) {
    if (metric.name === 'LCP') {
      const attribution = metric.attribution
      return {
        debug_url: attribution.url,
        debug_time_to_first_byte: attribution.timeToFirstByte,
        debug_resource_load_delay: attribution.resourceLoadDelay,
        debug_resource_load_duration: attribution.resourceLoadDuration,
        debug_element_render_delay: attribution.elementRenderDelay,
        debug_target: attribution.element || '(not set)',
      }
    } else if (metric.name === 'INP') {
      const attribution = metric.attribution
      return {
        debug_event: attribution.interactionType,
        debug_time: Math.round(attribution.interactionTime),
        debug_load_state: attribution.loadState,
        debug_target: attribution.interactionTarget || '(not set)',
        debug_interaction_delay: Math.round(attribution.inputDelay),
        debug_processing_duration: Math.round(attribution.processingDuration),
        debug_presentation_delay: Math.round(attribution.presentationDelay),
      }
    } else if (metric.name === 'CLS') {
      const attribution = metric.attribution
      return {
        debug_time: attribution.largestShiftTime,
        debug_load_state: attribution.loadState,
        debug_target: attribution.largestShiftTarget || '(not set)',
      }
    }
  }

  return {
    debug_target: '(not set)',
  }
}

export const resetWebVitalsMetrics = (state?: string) => {
  const event = new PageTransitionEvent('pageshow', { persisted: true })

  window.addEventListener(
    'pageshow',
    () => {
      recordPageTransition(Router.asPath, state)
    },
    { once: true },
  )
  window.dispatchEvent(event)
}

type TransitionRecord = { pathname: string; state?: string }

const pageTransitionRecords = new Map<number, TransitionRecord>()

const recordPageTransition = (pathname: string, state?: string) => {
  pageTransitionRecords.set(performance.now(), { pathname, state })

  // remove records older than 10 seconds but keeps last 3 records
  for (const [time] of Array.from(pageTransitionRecords).slice(0, -3)) {
    if (time < performance.now() - 10000) {
      pageTransitionRecords.delete(time)
    }
  }
}

const getPageLocationAt = (time: number = Infinity) => {
  const getPageLocation = ({ pathname, state }: TransitionRecord) => {
    const url = new URL(window.location.href)
    url.pathname = pathname
    if (state) url.hash = state
    return url.href
  }

  let prev: TransitionRecord | undefined
  for (const [t, record] of pageTransitionRecords) {
    if (prev && t > time) return getPageLocation(prev)

    prev = record
  }

  return getPageLocation({ pathname: Router.asPath })
}
